import { LevelDefinition, validateLevelDefinition } from "@algoquest/schemas";
import { world01SearchingFundamentalsLevels } from "./world01SearchingFundamentals";
import { world02LinearSearchLevels } from "./world02LinearSearch";
import { world03BinarySearchLevels } from "./world03BinarySearch";
import { world04SearchChallengeLevels } from "./world04SearchChallenge";
import { world05SortingFundamentalsLevels } from "./world05SortingFundamentals";
import { world06BubbleSortLevels } from "./world06BubbleSort";
import { world07SelectionSortLevels } from "./world07SelectionSort";
import { world08InsertionSortLevels } from "./world08InsertionSort";
import { world09SortingChallengeLevels } from "./world09SortingChallenge";
import { world10AlgorithmLabLevels } from "./world10AlgorithmLab";

/**
 * Phase 15 -- the full 100-level official campaign (Master Spec Section 5,
 * Section 41), aggregated from the 10 per-world files in this directory in
 * campaign order (World 1 levels 1-10 through World 10 levels 91-100).
 * The game client and database seed both consume this canonical catalog.
 */
export const campaignLevels: LevelDefinition[] = [
  ...world01SearchingFundamentalsLevels,
  ...world02LinearSearchLevels,
  ...world03BinarySearchLevels,
  ...world04SearchChallengeLevels,
  ...world05SortingFundamentalsLevels,
  ...world06BubbleSortLevels,
  ...world07SelectionSortLevels,
  ...world08InsertionSortLevels,
  ...world09SortingChallengeLevels,
  ...world10AlgorithmLabLevels,
];

/**
 * Structural + cross-field invariant checks beyond what Zod's shape alone
 * enforces -- run by `database/seeds/levels.ts` before writing anything to
 * the database, so a content-authoring mistake fails loudly at seed time
 * rather than silently reaching `game_levels`. Returns a list of problems;
 * an empty list means the campaign is safe to seed.
 */
export function validateCampaignLevels(): string[] {
  const problems: string[] = [];

  if (campaignLevels.length !== 100) {
    problems.push(`expected exactly 100 campaign levels, found ${campaignLevels.length}`);
  }

  const seenIds = new Set<string>();
  const seenGlobalNumbers = new Set<number>();
  for (const level of campaignLevels) {
    if (seenIds.has(level.id)) {
      problems.push(`duplicate level id: ${level.id}`);
    }
    seenIds.add(level.id);

    const g = level.coordinates.globalLevelNumber;
    if (seenGlobalNumbers.has(g)) {
      problems.push(`duplicate globalLevelNumber: ${g}`);
    }
    seenGlobalNumbers.add(g);

    const expectedGlobal = (level.coordinates.world - 1) * 10 + level.coordinates.levelInWorld;
    if (g !== expectedGlobal) {
      problems.push(
        `level ${level.id}: globalLevelNumber ${g} does not match world ${level.coordinates.world} / levelInWorld ${level.coordinates.levelInWorld} (expected ${expectedGlobal})`,
      );
    }

    const crossFieldErrors = validateLevelDefinition(level);
    for (const error of crossFieldErrors) {
      problems.push(`level ${level.id}: ${error}`);
    }
  }

  for (let g = 1; g <= 100; g += 1) {
    if (!seenGlobalNumbers.has(g)) {
      problems.push(`missing globalLevelNumber: ${g}`);
    }
  }

  return problems;
}
